import { lazy, useState, Suspense, useEffect } from "react";

import { useDarkMode } from "usehooks-ts";

const GdExercise = lazy(() => import("@/components/GdExercise.tsx"));

interface GdExerciseQueryParams {
  origin: string;
  dataPath: string;
  project: string;
  mainScene: string;
  files: Array<{
    path: string;
    focus: boolean;
    override?: string;
  }>;
  hidden: Array<"code" | "console" | "player" | "test">;
  snippet?: string;
}

function useDisplaySplash() {
  const [displaySplash, setDisplaySplash] = useState<boolean>(true);

  useEffect(() => {
    const splashContainer = document.querySelector<HTMLElement>(
      ".gd-splash-container"
    );
    if (splashContainer == null) return;

    splashContainer.hidden = !displaySplash;
  }, [displaySplash]);

  return {
    displaySplash,
    setDisplaySplash,
  };
}

async function loadGdExercise() {
  await import("@/components/GdExercise.tsx");
}

function useFileSizes() {
  const [fileSizes, setFileSizes] = useState<Record<string, number>>({});

  (async () => {
    const { default: size } = await import("@/generated/size.json");
    const localSize = Object.assign(
      {},
      ...Object.entries(size).map(([file, size]) => {
        // Prepend the current url location
        const url = new URL(file, window.location.href);
        return {
          [url.href]: size,
        };
      })
    );
    setFileSizes(localSize);
  })().catch((err) => console.error("Error loading sizes", err));

  return { fileSizes };
}

function useAppDarkMode() {
  const { isDarkMode } = useDarkMode();
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
    } else {
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
    }
  }, [isDarkMode]);
}

export default function App() {
  useAppDarkMode();

  const { fileSizes } = useFileSizes();
  const { setDisplaySplash } = useDisplaySplash();

  const [search, setSearch] = useState<string>(window.location.search);
  const [gdExerciseQueryParams, setGdExerciseQueryParams] =
    useState<GdExerciseQueryParams>({
      origin: window.location.origin,
      dataPath: "",
      project: "./",
      mainScene: "",
      files: [],
      hidden: [],
      snippet: "",
    });
  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    setGdExerciseQueryParams({
      origin: searchParams.get("origin") ?? window.location.origin,
      dataPath: searchParams.get("dataPath") ?? "",
      project: searchParams.get("project") ?? "./",
      mainScene: searchParams.get("mainScene") ?? "",
      files: searchParams.getAll("open").map((openFilePath) => {
        const [path, focus, override] = openFilePath.split(":") as Array<
          string | undefined
        >;

        const isFocus = () => {
          if (typeof focus !== "string") return false;
          if (focus.toLowerCase() === "focus") return true;
          if (focus.toLowerCase() === "nofocus") return false;
          throw new Error(`Unknown focus option: ${focus}`);
        };

        return {
          path: path as string,
          focus: isFocus(),
          override,
        };
      }),
      hidden: searchParams.getAll("hide") as GdExerciseQueryParams["hidden"],
      snippet: searchParams.get("snippet") ?? "",
    });
  }, [search]);

  const [isReady, setIsReady] = useState<boolean>(false);
  useEffect(() => {
    setIsReady(
      gdExerciseQueryParams.origin !== "" &&
        gdExerciseQueryParams.dataPath !== "" &&
        gdExerciseQueryParams.project !== "" &&
        gdExerciseQueryParams.mainScene !== "" &&
        Array.isArray(gdExerciseQueryParams.files)
    );
  }, [gdExerciseQueryParams]);

  window.addEventListener("hashchange", () => {
    setSearch(window.location.search);
  });

  useEffect(() => {
    // Force the load/importation of GdExercise
    loadGdExercise().catch((err) => {
      console.error("Error while importing GdExercise", err);
    });
  }, []);

  const onReady = () => {
    setDisplaySplash(false);
  };

  return (
    <>
      <Suspense fallback={<p>Loading...</p>}>
        {isReady && (
          <GdExercise
            origin={gdExerciseQueryParams.origin}
            dataPath={gdExerciseQueryParams.dataPath}
            project={gdExerciseQueryParams.project}
            mainScene={gdExerciseQueryParams.mainScene}
            files={gdExerciseQueryParams.files}
            size={fileSizes}
            onReady={onReady}
            hideCode={gdExerciseQueryParams.hidden.includes("code")}
            hideConsole={gdExerciseQueryParams.hidden.includes("console")}
            hidePlayer={gdExerciseQueryParams.hidden.includes("player")}
            hideTest={gdExerciseQueryParams.hidden.includes("test")}
            snippet={gdExerciseQueryParams.snippet}
          />
        )}
      </Suspense>
    </>
  );
}
